import { useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { Link } from '@/components/layout/TransitionLink'
import { Skeleton } from '@/components/ui/skeleton'
import { AdventureDial } from '@/components/build/AdventureDial'
import { Cover, TagChip } from '@/components/build/parts'
import { randomSeed, type PlaylistOptions } from '@/components/build/types'
import { SectionHeading } from '@/components/dashboard/fx/SectionHeading'
import { SpotlightCard } from '@/components/dashboard/fx/SpotlightCard'
import { Reveal } from '@/components/dashboard/fx/Reveal'
import { SECTION } from '@/components/dashboard/fx/layout'
import { useRideTeaser } from '@/hooks/useRideTeaser'
import { buildUrl, pickRecipe } from '@/lib/buildLink'

const TEASER_SONGS = 6

interface RideTeaserProps {
  instructorId: string
  name: string
  options: PlaylistOptions | null
  optionsFailed: boolean
}

// The instructor page's way into the playlist builder: not a button, a
// sample. Drag from "their greatest hits" to "songs they've never played" and
// watch the setlist change, then open exactly that ride in the full builder.
export function RideTeaser({ instructorId, name, options, optionsFailed }: RideTeaserProps) {
  const [adventure, setAdventure] = useState(0.5)
  const [seed] = useState(randomSeed)
  const recipe = pickRecipe(options)
  const { picks, loading, error } = useRideTeaser(instructorId, recipe, adventure, seed)

  // Nothing buildable (or the options failed): say nothing rather than tease a dead end.
  if (optionsFailed || (options && !recipe)) return null

  const firstName = name.split(' ')[0]
  const shown = picks?.slice(0, TEASER_SONGS) ?? null

  return (
    <section className={SECTION}>
      <SectionHeading
        index="05"
        title="Ride with"
        accent={`${firstName}'s music.`}
        blurb={`A taste of the playlist builder: a ${recipe?.minutes ?? 30}-minute ${recipe?.discipline ?? 'cycling'} ride from what ${firstName} really plays, nudged toward what they never do.`}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <div className="flex flex-col gap-4 lg:col-span-2">
          <AdventureDial value={adventure} onChange={setAdventure} name={firstName} regenerating={loading && picks !== null} />
          {recipe && (
            <Link
              to={buildUrl(instructorId, recipe, { adventure, seed })}
              className="group inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-5 py-3.5 text-sm font-medium text-primary-foreground transition-transform hover:-translate-y-0.5"
            >
              Open this ride in the builder
              <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
            </Link>
          )}
        </div>

        <Reveal className="lg:col-span-3">
          <SpotlightCard className="p-4 sm:p-5">
            {error && !shown ? (
              <p className="p-4 text-sm text-muted-foreground">Couldn&apos;t deal a sample ride just now.</p>
            ) : shown ? (
              <ol className={`flex flex-col gap-1 transition-opacity ${loading ? 'opacity-60' : 'opacity-100'}`}>
                {shown.map((item, index) => (
                  <li key={`${index}-${item.key}`} className="flex items-center gap-3 rounded-xl p-2">
                    <span className="w-5 shrink-0 text-right font-mono text-xs text-muted-foreground">{index + 1}</span>
                    <div className="size-11 shrink-0 overflow-hidden rounded-lg">
                      <Cover item={item} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{item.title}</div>
                      <div className="truncate text-xs text-muted-foreground">{item.artists.join(', ')}</div>
                    </div>
                    <TagChip tag={item.tag} className="hidden shrink-0 sm:inline-flex" />
                  </li>
                ))}
                <li className="px-2 pt-2 text-xs text-muted-foreground">…and the rest of the ride, in the full builder.</li>
              </ol>
            ) : (
              <div className="flex flex-col gap-2">
                {Array.from({ length: TEASER_SONGS }).map((_, i) => (
                  <Skeleton key={i} className="h-14 w-full rounded-xl" />
                ))}
              </div>
            )}
          </SpotlightCard>
        </Reveal>
      </div>
    </section>
  )
}
